import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  LuChevronDown,
  LuFlame,
  LuFolderOpen,
  LuGlobe,
  LuLogOut,
  LuMenu,
  LuNetwork,
  LuSearch,
  LuShield,
  LuSparkles,
  LuTrophy,
  LuX,
} from 'react-icons/lu';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useUserContext } from './context/useUserContext';
import { getUsernameFromToken } from './utils';
import { AppRoutes } from './appRoutes';
import { StatsAvailable } from './types';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface MenuItemDef {
  key: string;
  label: string;
  path: string;
  onClick: () => void;
  category?: 'Top' | 'Fun' | 'Viz' | 'Archive';
}

export const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const token = localStorage.getItem('loginToken');
  const showMenu = Boolean(token && token.length > 0);
  const username = getUsernameFromToken();
  const { users, getUserColor } = useUserContext();

  const currentUser = users.find(u => u.name === username);
  const isAdmin = currentUser?.admin;
  const userColor = username ? getUserColor(username) : '#ec4899';

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const menuItems: MenuItemDef[] = [
    ...Object.keys(StatsAvailable).map(key => ({
      key,
      label: StatsAvailable[key].title,
      path: '/stats/' + key,
      onClick: () => navigate('/stats/' + key),
      category:
        StatsAvailable[key].category ??
        (key === 'word_count' ? ('Archive' as const) : ('Top' as const)),
    })),
    {
      key: 'heat',
      label: 'Heat 🔥',
      path: '/heat',
      onClick: () => navigate('/heat'),
      category: 'Viz',
    },
    {
      key: 'map',
      label: 'World Map 🌎',
      path: '/map',
      onClick: () => navigate('/map'),
      category: 'Viz',
    },
    {
      key: 'influence',
      label: 'Social Flux ⚛️',
      path: '/influence',
      onClick: () => navigate('/influence'),
      category: 'Viz',
    },
    {
      key: 'chart',
      label: 'Activity Over Time 📈',
      path: '/chart',
      onClick: () => navigate('/chart'),
      category: 'Viz',
    },
    {
      key: 'time_of_day',
      label: 'Time of Day ⏰',
      path: '/time_of_day',
      onClick: () => navigate('/time_of_day'),
      category: 'Viz',
    },
    {
      key: 'attachments',
      label: 'Attachments 📎',
      path: '/attachments',
      onClick: () => navigate('/attachments'),
      category: 'Archive',
    },
    {
      key: 'ohsnap365',
      label: 'ohsnap365 Archive 📸',
      path: '/ohsnap365',
      onClick: () => navigate('/ohsnap365'),
      category: 'Archive',
    },
    {
      key: 'address_book',
      label: 'Address Book 📒',
      path: '/address_book',
      onClick: () => navigate('/address_book'),
      category: 'Archive',
    },
  ];

  const topItems = menuItems.filter(i => i.category === 'Top');
  const funItems = menuItems.filter(i => i.category === 'Fun');
  const vizItems = menuItems.filter(i => i.category === 'Viz');
  const archiveItems = menuItems.filter(i => i.category === 'Archive');

  const isFullViewportRoute =
    location.pathname.startsWith('/map') ||
    location.pathname.startsWith('/influence') ||
    location.pathname.startsWith('/message_tree');

  const glassMenuContentProps = {
    bg: 'rgba(13, 17, 30, 0.94)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 'xl',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.75)',
    p: 1.5,
    minW: '240px',
    zIndex: 1500,
  };

  return (
    <Flex
      flexDirection='column'
      minH='100vh'
      h={isFullViewportRoute ? '100vh' : undefined}
      bg='#070910'
      color='white'
      position='relative'
      overflowX='hidden'
    >
      {/* Global Ambient Aurora Mesh Background */}
      <Box
        position='fixed'
        inset={0}
        pointerEvents='none'
        zIndex={0}
        overflow='hidden'
      >
        <Box
          position='absolute'
          top='-15%'
          left='-10%'
          w='55vw'
          h='55vw'
          borderRadius='full'
          bg='radial-gradient(circle, rgba(124, 58, 237, 0.14) 0%, transparent 70%)'
          filter='blur(60px)'
        />
        <Box
          position='absolute'
          top='20%'
          right='-15%'
          w='50vw'
          h='50vw'
          borderRadius='full'
          bg='radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%)'
          filter='blur(70px)'
        />
        <Box
          position='absolute'
          bottom='-20%'
          left='25%'
          w='60vw'
          h='60vw'
          borderRadius='full'
          bg='radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)'
          filter='blur(80px)'
        />
      </Box>

      {/* Sticky 2026 Glass Navigation Header */}
      {showMenu && (
        <Box
          as='header'
          position='sticky'
          top={0}
          zIndex={1000}
          px={{ base: 3, md: 6 }}
          py={2.5}
          bg='rgba(9, 12, 22, 0.78)'
          backdropFilter='blur(20px)'
          borderBottom='1px solid rgba(255, 255, 255, 0.08)'
          boxShadow='0 8px 32px rgba(0, 0, 0, 0.35)'
        >
          <Flex
            maxW='1440px'
            mx='auto'
            align='center'
            justify='space-between'
            gap={3}
          >
            {/* Brand Logo */}
            <HStack
              gap={3}
              cursor='pointer'
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.92 }}
              transition='opacity 0.2s'
            >
              <motion.div
                whileHover={{ rotate: [-5, 8, 0], scale: 1.08 }}
                transition={{ duration: 0.35 }}
              >
                <Image
                  src='/logo192.png'
                  alt='DeadSquirrel Logo'
                  boxSize={{ base: '38px', md: '44px' }}
                  borderRadius='xl'
                  boxShadow='0 0 20px rgba(236, 72, 153, 0.35)'
                />
              </motion.div>
              <VStack align='start' gap={0}>
                <HStack gap={1.5}>
                  <Text
                    fontWeight='800'
                    fontSize={{ base: 'md', md: 'lg' }}
                    letterSpacing='-0.03em'
                    bgGradient='to-r'
                    gradientFrom='white'
                    gradientVia='pink.200'
                    gradientTo='purple.300'
                    bgClip='text'
                  >
                    DeadSquirrel
                  </Text>
                  <Box
                    px={1.5}
                    py={0.5}
                    fontSize='2xs'
                    fontWeight='700'
                    borderRadius='md'
                    bg='rgba(236, 72, 153, 0.18)'
                    color='pink.300'
                    border='1px solid rgba(236, 72, 153, 0.3)'
                    display={{ base: 'none', sm: 'inline-block' }}
                  >
                    2026
                  </Box>
                </HStack>
                <Text
                  fontSize='2xs'
                  color='gray.400'
                  letterSpacing='0.04em'
                  display={{ base: 'none', sm: 'block' }}
                >
                  TELEMETRY & ARCHIVES
                </Text>
              </VStack>
            </HStack>

            {/* Desktop Navigation */}
            <HStack gap={1.5} display={{ base: 'none', lg: 'flex' }}>
              {/* Top Stats Menu */}
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    color='gray.100'
                    borderRadius='xl'
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.08)',
                      color: 'pink.200',
                    }}
                  >
                    <LuTrophy color='#f472b6' />
                    Leaderboards
                    <LuChevronDown />
                  </Button>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent {...glassMenuContentProps}>
                    {topItems.map(item => (
                      <MenuItem
                        key={item.key}
                        value={item.key}
                        onClick={item.onClick}
                        borderRadius='lg'
                        py={2}
                        px={3}
                        color={
                          location.pathname === item.path ? 'pink.300' : 'white'
                        }
                        bg={
                          location.pathname === item.path
                            ? 'rgba(236, 72, 153, 0.15)'
                            : 'transparent'
                        }
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                        cursor='pointer'
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>

              {/* Fun Stats Menu */}
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    color='gray.100'
                    borderRadius='xl'
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.08)',
                      color: 'purple.200',
                    }}
                  >
                    <LuSparkles color='#c084fc' />
                    Fun & Quirky
                    <LuChevronDown />
                  </Button>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent {...glassMenuContentProps} maxH='70vh' overflowY='auto'>
                    {funItems.map(item => (
                      <MenuItem
                        key={item.key}
                        value={item.key}
                        onClick={item.onClick}
                        borderRadius='lg'
                        py={2}
                        px={3}
                        color={
                          location.pathname === item.path
                            ? 'purple.300'
                            : 'white'
                        }
                        bg={
                          location.pathname === item.path
                            ? 'rgba(168, 85, 247, 0.15)'
                            : 'transparent'
                        }
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                        cursor='pointer'
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>

              {/* Visualizations Menu */}
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    color='gray.100'
                    borderRadius='xl'
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.08)',
                      color: 'cyan.200',
                    }}
                  >
                    <LuNetwork color='#22d3ee' />
                    Visualizations
                    <LuChevronDown />
                  </Button>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent {...glassMenuContentProps}>
                    {vizItems.map(item => (
                      <MenuItem
                        key={item.key}
                        value={item.key}
                        onClick={item.onClick}
                        borderRadius='lg'
                        py={2}
                        px={3}
                        color={
                          location.pathname === item.path ? 'cyan.300' : 'white'
                        }
                        bg={
                          location.pathname === item.path
                            ? 'rgba(6, 182, 212, 0.15)'
                            : 'transparent'
                        }
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                        cursor='pointer'
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>

              {/* Archives Menu */}
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    color='gray.100'
                    borderRadius='xl'
                    _hover={{
                      bg: 'rgba(255, 255, 255, 0.08)',
                      color: 'pink.200',
                    }}
                  >
                    <LuFolderOpen color='#f472b6' />
                    Archives
                    <LuChevronDown />
                  </Button>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent {...glassMenuContentProps}>
                    {archiveItems.map(item => (
                      <MenuItem
                        key={item.key}
                        value={item.key}
                        onClick={item.onClick}
                        borderRadius='lg'
                        py={2}
                        px={3}
                        color={
                          location.pathname.startsWith(item.path)
                            ? 'pink.300'
                            : 'white'
                        }
                        bg={
                          location.pathname.startsWith(item.path)
                            ? 'rgba(236, 72, 153, 0.15)'
                            : 'transparent'
                        }
                        _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                        cursor='pointer'
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>

              {/* Quick Search Button */}
              <Button
                variant='ghost'
                size='sm'
                color='gray.200'
                borderRadius='xl'
                onClick={() => navigate('/stats/word_count')}
                _hover={{ bg: 'rgba(255, 255, 255, 0.08)', color: 'white' }}
              >
                <LuSearch />
                Word Search
              </Button>

              {/* Quick Heat Button */}
              <Button
                variant='ghost'
                size='sm'
                color='gray.200'
                borderRadius='xl'
                onClick={() => navigate('/heat')}
                _hover={{ bg: 'rgba(255, 255, 255, 0.08)', color: 'white' }}
              >
                <LuFlame color='#fb923c' />
                Heat
              </Button>

              {/* Quick Map Button */}
              <Button
                variant='ghost'
                size='sm'
                color='gray.200'
                borderRadius='xl'
                onClick={() => navigate('/map')}
                _hover={{ bg: 'rgba(255, 255, 255, 0.08)', color: 'white' }}
              >
                <LuGlobe color='#38bdf8' />
                Map
              </Button>

              {isAdmin && (
                <Button
                  variant='ghost'
                  size='sm'
                  color='yellow.300'
                  borderRadius='xl'
                  onClick={() => navigate('/admin')}
                  _hover={{ bg: 'rgba(251, 191, 36, 0.12)' }}
                >
                  <LuShield />
                  Admin
                </Button>
              )}
            </HStack>

            {/* User Pill + Mobile Menu Button */}
            <HStack gap={2}>
              {username && (
                <HStack
                  px={3}
                  py={1.5}
                  borderRadius='full'
                  bg='rgba(255, 255, 255, 0.05)'
                  border='1px solid rgba(255, 255, 255, 0.1)'
                  gap={2}
                >
                  <Box
                    w='10px'
                    h='10px'
                    borderRadius='full'
                    bg={userColor}
                    boxShadow={`0 0 8px ${userColor}`}
                  />
                  <Text
                    fontSize='xs'
                    fontWeight='600'
                    color='gray.200'
                    display={{ base: 'none', sm: 'block' }}
                  >
                    {username}
                  </Text>
                  <IconButton
                    aria-label={`Logout of ${username}`}
                    title={`Logout of ${username}`}
                    size='2xs'
                    variant='ghost'
                    color='gray.400'
                    _hover={{ color: 'red.300', bg: 'rgba(239, 68, 68, 0.15)' }}
                    onClick={() => navigate('/logout')}
                  >
                    <LuLogOut />
                  </IconButton>
                </HStack>
              )}

              {/* Mobile Hamburger Trigger */}
              <IconButton
                aria-label='Toggle Navigation Menu'
                display={{ base: 'flex', lg: 'none' }}
                variant='ghost'
                color='white'
                borderRadius='xl'
                bg='rgba(255, 255, 255, 0.06)'
                border='1px solid rgba(255, 255, 255, 0.1)'
                onClick={() => setMobileMenuOpen(prev => !prev)}
              >
                {mobileMenuOpen ? <LuX /> : <LuMenu />}
              </IconButton>
            </HStack>
          </Flex>

          {/* Animated Mobile Navigation Sheet */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
              >
                <Box
                  pt={4}
                  pb={3}
                  mt={3}
                  borderTop='1px solid rgba(255, 255, 255, 0.08)'
                  maxH='78vh'
                  overflowY='auto'
                >
                  <VStack align='stretch' gap={4}>
                    {/* Quick Action Bar */}
                    <SimpleGrid columns={2} gap={2}>
                      <Button
                        size='sm'
                        bg='rgba(236, 72, 153, 0.15)'
                        color='pink.200'
                        border='1px solid rgba(236, 72, 153, 0.3)'
                        borderRadius='xl'
                        onClick={() => navigate('/stats/word_count')}
                      >
                        <LuSearch /> Word Search
                      </Button>
                      <Button
                        size='sm'
                        bg='rgba(6, 182, 212, 0.15)'
                        color='cyan.200'
                        border='1px solid rgba(6, 182, 212, 0.3)'
                        borderRadius='xl'
                        onClick={() => navigate('/attachments')}
                      >
                        <LuFolderOpen /> Attachments
                      </Button>
                    </SimpleGrid>

                    {/* Visualizations & Archives */}
                    <Box>
                      <Text
                        fontSize='xs'
                        fontWeight='700'
                        color='cyan.300'
                        textTransform='uppercase'
                        letterSpacing='0.08em'
                        mb={2}
                      >
                        🌐 Visualizations & Archives
                      </Text>
                      <SimpleGrid columns={{ base: 2, sm: 3 }} gap={1.5}>
                        {[...vizItems, ...archiveItems].map(item => (
                          <Button
                            key={item.key}
                            size='xs'
                            justifyContent='flex-start'
                            variant='ghost'
                            bg='rgba(255, 255, 255, 0.04)'
                            color={
                              location.pathname === item.path
                                ? 'cyan.300'
                                : 'gray.200'
                            }
                            borderRadius='lg'
                            onClick={item.onClick}
                          >
                            {item.label}
                          </Button>
                        ))}
                        {isAdmin && (
                          <Button
                            size='xs'
                            justifyContent='flex-start'
                            variant='ghost'
                            bg='rgba(251, 191, 36, 0.12)'
                            color='yellow.300'
                            borderRadius='lg'
                            onClick={() => navigate('/admin')}
                          >
                            🛠️ Admin
                          </Button>
                        )}
                      </SimpleGrid>
                    </Box>

                    {/* Top Leaderboards */}
                    <Box>
                      <Text
                        fontSize='xs'
                        fontWeight='700'
                        color='pink.300'
                        textTransform='uppercase'
                        letterSpacing='0.08em'
                        mb={2}
                      >
                        🏆 Top Leaderboards
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={1.5}>
                        {topItems.map(item => (
                          <Button
                            key={item.key}
                            size='xs'
                            justifyContent='flex-start'
                            variant='ghost'
                            bg='rgba(255, 255, 255, 0.04)'
                            color={
                              location.pathname === item.path
                                ? 'pink.300'
                                : 'gray.200'
                            }
                            borderRadius='lg'
                            onClick={item.onClick}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </SimpleGrid>
                    </Box>

                    {/* Fun Stats */}
                    <Box>
                      <Text
                        fontSize='xs'
                        fontWeight='700'
                        color='purple.300'
                        textTransform='uppercase'
                        letterSpacing='0.08em'
                        mb={2}
                      >
                        🎉 Fun & Quirky Stats
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={1.5}>
                        {funItems.map(item => (
                          <Button
                            key={item.key}
                            size='xs'
                            justifyContent='flex-start'
                            variant='ghost'
                            bg='rgba(255, 255, 255, 0.04)'
                            color={
                              location.pathname === item.path
                                ? 'purple.300'
                                : 'gray.200'
                            }
                            borderRadius='lg'
                            onClick={item.onClick}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </SimpleGrid>
                    </Box>

                    {username && (
                      <Button
                        size='sm'
                        variant='outline'
                        borderColor='rgba(239, 68, 68, 0.35)'
                        color='red.300'
                        borderRadius='xl'
                        onClick={() => navigate('/logout')}
                      >
                        <LuLogOut /> Logout of {username}
                      </Button>
                    )}
                  </VStack>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      )}

      {/* Main Content Area */}
      <Box flex={1} position='relative' zIndex={1} display='flex' flexDirection='column'>
        <AppRoutes />
      </Box>

      <ToastContainer theme='dark' position='bottom-right' />
    </Flex>
  );
};
